import { useEffect, useState } from 'preact/hooks';
import { useLocation, useRoute } from 'preact-iso';
import { api, type EventDetail as EventDetailT } from '../lib/api.js';

function toLocalInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function EventEdit() {
  const { params } = useRoute();
  const loc = useLocation();
  const id = params.id!;

  const [title, setTitle] = useState('');
  const [location, setLocation] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api<EventDetailT>(`/api/v1/events/${id}`)
      .then((e) => {
        setTitle(e.title);
        setLocation(e.location ?? '');
        setStartsAt(toLocalInput(new Date(e.startsAt)));
        setEndsAt(toLocalInput(new Date(e.endsAt)));
        setNotes(e.notes ?? '');
      })
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }, [id]);

  function onStartsChange(v: string) {
    setStartsAt(v);
    const ns = new Date(v);
    const ne = new Date(endsAt);
    if (isNaN(ns.getTime())) return;
    if (isNaN(ne.getTime()) || ne.getTime() <= ns.getTime() || ne.getTime() - ns.getTime() < 15 * 60_000) {
      setEndsAt(toLocalInput(new Date(ns.getTime() + 60 * 60_000)));
    }
  }

  function onEndsChange(v: string) {
    const ne = new Date(v);
    const ns = new Date(startsAt);
    if (!isNaN(ne.getTime()) && !isNaN(ns.getTime()) && ne.getTime() <= ns.getTime()) {
      setEndsAt(toLocalInput(new Date(ns.getTime() + 60 * 60_000)));
      return;
    }
    setEndsAt(v);
  }

  async function submit(ev: Event) {
    ev.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await api(`/api/v1/events/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          title,
          location: location || null,
          startsAt: new Date(startsAt).toISOString(),
          endsAt: new Date(endsAt).toISOString(),
          notes: notes || null,
        }),
      });
      loc.route(`/event/${id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <div class="screen" style={{ textAlign: 'center' }}><span class="spinner" /></div>;

  return (
    <div class="screen">
      <h1>Termin bearbeiten</h1>
      <p class="muted" style={{ marginTop: -8 }}>Basisdaten. Participants-Änderungen folgen in v2.5.</p>

      <form onSubmit={submit}>
        <div class="field">
          <label>Titel</label>
          <input required value={title} onInput={(e) => setTitle((e.target as HTMLInputElement).value)} />
        </div>
        <div class="field">
          <label>Ort</label>
          <input value={location} onInput={(e) => setLocation((e.target as HTMLInputElement).value)} />
        </div>
        <div class="row">
          <div class="field">
            <label>Von</label>
            <input type="datetime-local" required value={startsAt} onInput={(e) => onStartsChange((e.target as HTMLInputElement).value)} />
          </div>
          <div class="field">
            <label>Bis</label>
            <input type="datetime-local" required value={endsAt} min={startsAt} onInput={(e) => onEndsChange((e.target as HTMLInputElement).value)} />
          </div>
        </div>
        <div class="field">
          <label>Notizen</label>
          <textarea rows={3} value={notes} onInput={(e) => setNotes((e.target as HTMLTextAreaElement).value)} />
        </div>

        {error ? <div class="error">{error}</div> : null}

        <div class="row" style={{ marginTop: 10 }}>
          <button type="button" class="ghost" onClick={() => loc.route(`/event/${id}`)}>Abbrechen</button>
          <button type="submit" disabled={saving}>{saving ? <span class="spinner" /> : 'Speichern'}</button>
        </div>
      </form>
    </div>
  );
}
